// Mock chat data for Phase 1 of the chat port.
//
// Shape mirrors what `session.list` + `session.load` will eventually
// return from the real backend (see lib/use-chat.ts when that lands).
// Delete this file once the chat view is wired to /api/rpc.

export interface MockMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

export interface MockThread {
  id: string;
  title: string;
  updated: string;
  model: string;
  pinned?: boolean;
  messages: MockMessage[];
}

export const MOCK_THREADS: MockThread[] = [
  {
    id: "c-1",
    title: "Friday's plan",
    updated: "12m",
    model: "Vulture Chat 4",
    pinned: true,
    messages: [
      { id: "m-1", role: "user",      text: "Plan a quiet Friday for me — work done by 4, then nothing scheduled." },
      { id: "m-2", role: "assistant", text: "Sounds nice. **Three options** depending on energy:\n\n1. Slow cook + a movie at home.\n2. Walk somewhere with golden-hour light, dinner out.\n3. Skip plans entirely. The point is *unscheduled*.\n\nWhich way are you leaning?" },
    ],
  },
  {
    id: "c-2",
    title: "Re-read of the lease",
    updated: "3h",
    model: "Vulture Chat 4",
    pinned: true,
    messages: [
      { id: "m-3", role: "user",      text: "Read this lease and flag the parts I'd regret missing." },
      { id: "m-4", role: "assistant", text: "Two clauses worth a second look:\n\n• **Section 7c** — early-termination fee scales with months remaining, not flat.\n• **Section 11** — landlord-entry notice is 24h, not the 48h default.\n\nWant me to draft pushback language for either?" },
    ],
  },
  {
    id: "c-3",
    title: "Lemon pasta variations",
    updated: "yesterday",
    model: "Vulture Chat 4",
    messages: [
      { id: "m-5", role: "user",      text: "Three takes on lemon pasta — light to rich." },
      { id: "m-6", role: "assistant", text: "Light → just pasta water, lemon zest, olive oil, parmesan.\nMedium → add a soft-yolk-stage egg and pepper, carbonara-ish.\nRich → add browned butter and chopped capers." },
    ],
  },
  {
    id: "c-4",
    title: "Two job offers",
    updated: "2d",
    model: "Vulture Chat 4",
    messages: [],
  },
  {
    id: "c-5",
    title: "Polite no to the wedding",
    updated: "1w",
    model: "Vulture Chat 4",
    messages: [],
  },
];

export const MOCK_CHAT_MODES = ["default", "plan", "acceptEdits"] as const;
export type ChatMode = (typeof MOCK_CHAT_MODES)[number];

export const MOCK_ENV = {
  model: "qwen3.6-27b-fp8",
  cwd: "~/projects",
};

export const MOCK_SKILLS_COUNT = { active: 3, total: 5 };
export const MOCK_PRESETS_COUNT = 4;

export const MOCK_SUGGESTIONS = [
  "Plan a low-effort dinner with what's in my fridge",
  "Help me write a polite no",
  "Read this contract and flag the weird bits",
  "Compare these two job offers honestly",
];

// Toy "thinking" reply used until we wire run.start + SSE in Phase 2.
export function simulatedReply(text: string): string {
  const lower = text.toLowerCase();
  if (lower.length < 12) return "Tell me a bit more — what does success look like here?";
  if (lower.includes("recipe") || lower.includes("dinner")) {
    return "Happy to. What's in your fridge right now, and how lazy are we feeling — 15 min, 30, or full project?";
  }
  if (lower.includes("?")) {
    return "Good question. Two ways to think about it:\n\n1. The narrow answer — directly addressing what you asked.\n2. The wider frame — what this is actually about underneath.\n\nWant me to start with one, or do both?";
  }
  return "Got it. Reading that back: you want " + text.split(/[.!?]/)[0].slice(0, 60) + "… Let me sketch a draft and we can refine.";
}
